import { FocusSummaryCard } from '@/shared/components';
import { formatCurrency } from '@/shared/lib/currency';
import { useFacilityScope } from '@/shared/lib/hooks/use-facility-scope';
import { shouldShowSparkline } from '@/shared/lib/sparkline-range';
import { useQuery } from '@tanstack/react-query';
import type { Dayjs } from 'dayjs';
import { type FC } from 'react';
import { getRevenueQueryOptions } from '../api/get-revenue.query';

interface RevenueSummaryCardProps {
  dateRange: {
    from: Dayjs;
    to: Dayjs;
  };
}

export const RevenueSummaryCard: FC<RevenueSummaryCardProps> = ({
  dateRange,
}) => {
  const { scopedFacilityId } = useFacilityScope();
  const { data, isLoading } = useQuery(
    getRevenueQueryOptions({
      from: dateRange.from.format('YYYY-MM-DD'),
      to: dateRange.to.format('YYYY-MM-DD'),
      facilitiesIds: scopedFacilityId ? [scopedFacilityId] : undefined,
    })
  );

  const daysDifference = dateRange.to.diff(dateRange.from, 'days');
  const showSparkline = shouldShowSparkline(dateRange);

  if (daysDifference < 27) {
    return (
      <FocusSummaryCard
        valueAsComment
        icon="dollar"
        title="Revenue"
        tooltipMessage="Total payments collected from residents"
        value="Revenue data is available only for date ranges of one month or longer."
        loading={false}
      />
    );
  }

  if (!isLoading && !data) {
    return (
      <FocusSummaryCard
        value="N/A"
        icon="dollar"
        title="Revenue"
        loading={false}
      />
    );
  }

  const amount = data?.revenue?.amountUsd;
  const changePct = data?.revenue?.changePct;
  const sparklinePoints = data?.sparkline?.points || [];

  const chartData = {
    labels: sparklinePoints.map((_, index) => index.toString()),
    datasets: [
      {
        data: sparklinePoints.map((point) => point.value),
        borderColor: '#165BAA',
        backgroundColor: 'transparent',
        fill: false,
      },
    ],
  };

  const indicator =
    changePct != null
      ? {
          value: changePct,
          negativeValue: changePct < 0,
        }
      : undefined;

  return (
    <FocusSummaryCard
      icon="dollar"
      title="Revenue"
      tooltipMessage="Total payments collected from residents"
      indicator={indicator}
      showChart={showSparkline}
      chart={showSparkline ? { data: chartData } : undefined}
      value={amount != null ? formatCurrency(amount) : 'N/A'}
      loading={isLoading}
    />
  );
};
